import React, { ReactNode, forwardRef, ElementType } from 'react';
import { Box, BoxProps, Typography, TypographyProps } from '@mui/material';
import { Label, LabelProps } from '@/src/components/label';

export interface TextAndCtaProps extends BoxProps<ElementType> {
  /**
   * Top label above the headlines
   */
  label?: ReactNode;
  /**
   * Label customization
   */
  labelProps?: LabelProps;
  /**
   * Main Headline at the top
   */
  headline?: ReactNode;
  /**
   * Headline customization
   */
  headlineProps?: TypographyProps<ElementType>;
  /**
   * Main Headline at the top
   */
  subheadline?: ReactNode;
  /**
   * Subheading customization
   */
  subheadlineProps?: TypographyProps<ElementType>;
  /**
   * Main text body
   */
  body?: ReactNode;
  /**
   * Body text customization
   */
  bodyProps?: TypographyProps<ElementType>;
  /**
   * Bottom CTA buttons
   */
  buttons?: ReactNode;
  /**
   * Body text customization
   */
  buttonsProps?: BoxProps<ElementType>;
}

export const TextAndCta: React.FC<TextAndCtaProps> = forwardRef(
  (
    {
      sx,
      children,
      label,
      labelProps,
      headline,
      headlineProps,
      subheadline,
      subheadlineProps,
      body,
      bodyProps,
      buttons,
      buttonsProps,
      ...rest
    },
    ref,
  ) => {
    const { sx: labelSx, ...restLabelProps } = labelProps || {};
    const { sx: headlineSx, ...restHeadlineProps } = headlineProps || {};
    const { sx: subHeadlineSx, ...restSubHeadlineProps } = subheadlineProps || {};
    const { sx: bodySx, ...restBodyProps } = bodyProps || {};
    const { sx: buttonsSx, ...restButtonsProps } = buttonsProps || {};

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          p: 3,
          width: '320px',
          maxWidth: '100%',
          ...sx,
        }}
        ref={ref}
        {...rest}
      >
        {label && (
          <Label sx={{ mb: 1, ...labelSx }} {...restLabelProps}>
            {label}
          </Label>
        )}
        {headline && (
          <Typography variant="h4" sx={{ display: 'block', mb: 1, ...headlineSx }} {...restHeadlineProps}>
            {headline}
          </Typography>
        )}
        {subheadline && (
          <Typography
            variant="subheadline"
            sx={{ display: 'block', mb: 1, ...subHeadlineSx }}
            {...restSubHeadlineProps}
          >
            {subheadline}
          </Typography>
        )}
        {body && (
          <Typography sx={{ display: 'block', mb: 2, ...bodySx }} {...restBodyProps}>
            {body}
          </Typography>
        )}
        {children}
        {buttons && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'stretch',
              alignSelf: 'stretch',
              gap: 1,
              ...buttonsSx,
            }}
            {...restButtonsProps}
          >
            {buttons}
          </Box>
        )}
      </Box>
    );
  },
);
